import { FormEvent, useEffect, useState } from "react";
import {
  CalendarDays,
  Pause,
  Play,
  Plus,
  Printer,
  RefreshCw,
  Wrench,
} from "lucide-react";
import { request } from "./FunctionalApp";

const title = (s: string = "") =>
  s
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (x) => x.toUpperCase());
const day = (s: string) => new Date(s).toLocaleDateString();
async function call(path: string, init: RequestInit = {}) {
  const r = await request(path, init),
    x = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(title(x.error || "Unable to save"));
  return x;
}
const post = (path: string, body: any = {}) =>
  call(path, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });

export function PpmWorkspace() {
  const [data, setData] = useState<any>({
      plans: [],
      occurrences: [],
      assets: [],
      users: [],
      canManage: false,
    }),
    [create, setCreate] = useState(false),
    [edit, setEdit] = useState<any>(null),
    [requirements, setRequirements] = useState<any>(null),
    [dismiss, setDismiss] = useState<any>(null),
    [message, setMessage] = useState("");
  async function load() {
    try {
      setData(await call("/api/ppm"));
    } catch (e: any) {
      setMessage(e.message);
    }
  }
  useEffect(() => {
    load();
  }, []);
  async function action(path: string, body: any = {}) {
    try {
      const x = await post(path, body);
      setMessage(x.number ? `${x.number} created from PPM` : "PPM updated");
      await load();
    } catch (e: any) {
      setMessage(e.message);
    }
  }
  async function releaseAndPrint(occurrenceId: string) {
    const popup = window.open("", "_blank");
    if (popup)
      popup.document.write(
        "<p style='font-family:sans-serif;padding:24px'>Creating and preparing PPM job sheet…</p>",
      );
    try {
      const released = await post(
        `/api/ppm/occurrences/${occurrenceId}/release`,
        {},
      );
      const response = await request(
        `/api/maintenance/work-orders/${released.workOrderId}/print`,
      );
      if (!response.ok) throw new Error("Unable to prepare PPM job sheet");
      const url = URL.createObjectURL(await response.blob());
      if (popup) popup.location.href = url;
      else window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      setMessage(`${released.number} created and ready to print`);
      await load();
    } catch (e: any) {
      popup?.close();
      setMessage(e.message);
    }
  }
  const overdue = data.occurrences.filter(
    (o: any) => new Date(o.due_at) < new Date(),
  ).length;
  return (
    <>
      <div className="func-heading">
        <div>
          <h1>Planned preventive maintenance</h1>
          <p>
            Schedule essential maintenance and release due work in a controlled
            way.
          </p>
        </div>
        {data.canManage ? (
          <button
            onClick={() => {
              setCreate(!create);
              setEdit(null);
            }}
          >
            <Plus />
            New PPM schedule
          </button>
        ) : null}
      </div>
      {message ? <div className="func-admin-note">{message}</div> : null}
      <div className="func-kpis">
        <div className="func-kpi">
          <span>Active schedules</span>
          <strong>{data.plans.filter((p: any) => p.active).length}</strong>
        </div>
        <div className="func-kpi">
          <span>Due occurrences</span>
          <strong>{data.occurrences.length}</strong>
        </div>
        <div className="func-kpi">
          <span>Overdue</span>
          <strong>{overdue}</strong>
        </div>
        <div className="func-kpi">
          <span>Paused</span>
          <strong>{data.plans.filter((p: any) => !p.active).length}</strong>
        </div>
      </div>
      {create || edit ? (
        <PpmForm
          value={edit}
          assets={data.assets}
          users={data.users}
          jobTypes={data.jobTypes || []}
          saved={async () => {
            setCreate(false);
            setEdit(null);
            setMessage("PPM schedule saved");
            await load();
          }}
        />
      ) : null}
      {requirements ? (
        <RequirementsEditor
          plan={requirements}
          close={() => setRequirements(null)}
          saved={() => {
            setRequirements(null);
            setMessage("PPM checklist and requirements saved");
          }}
        />
      ) : null}
      <section className="func-panel ppm-due">
        <div className="func-table-head">
          <h2>Due and overdue maintenance</h2>
          <button className="secondary" onClick={load}>
            <RefreshCw />
            Refresh
          </button>
        </div>
        {data.occurrences.length ? (
          data.occurrences.map((o: any) => (
            <article
              key={o.id}
              className={new Date(o.due_at) < new Date() ? "overdue" : ""}
            >
              <div>
                <CalendarDays />
                <span>
                  <b>{o.plan_name}</b>
                  <small>{o.asset_path}</small>
                </span>
              </div>
              <span>
                <b>Due {day(o.due_at)}</b>
                <small>
                  {title(o.priority)} ·{" "}
                  {o.estimated_minutes
                    ? `${o.estimated_minutes} minutes`
                    : "No duration set"}
                </small>
              </span>
              <em>
                {o.state === "DEFERRED_RESTRICTION"
                  ? "Reactivation catch-up"
                  : new Date(o.due_at) < new Date()
                    ? "Overdue"
                    : "Ready"}
              </em>
              {data.canManage ? (
                <div>
                  <button
                    onClick={() =>
                      action(`/api/ppm/occurrences/${o.id}/release`)
                    }
                  >
                    <Wrench />
                    Release work order
                  </button>
                  <button
                    className="secondary"
                    onClick={() => releaseAndPrint(o.id)}
                  >
                    <Printer />
                    Release &amp; print
                  </button>
                  <button
                    className="secondary"
                    onClick={() => setDismiss(o)}
                  >
                    Dismiss
                  </button>
                </div>
              ) : null}
            </article>
          ))
        ) : (
          <p className="func-empty">
            Nothing is due. The scheduler will add occurrences automatically.
          </p>
        )}
      </section>
      <section className="func-panel ppm-plans">
        <div className="func-table-head">
          <h2>PPM schedules</h2>
        </div>
        {data.plans.map((p: any) => (
          <article key={p.id}>
            <span>
              <b>{p.name}</b>
              <small>{p.asset_path}</small>
              <small>
                {p.job_type_name || "PPM"}
                {p.contractor ? " · Contractor" : ""}
              </small>
            </span>
            <span>
              <b>
                Every {p.interval_value} {title(p.interval_unit)}
                {p.interval_value > 1 ? "s" : ""}
              </b>
              <small>Next due {day(p.next_due_at)}</small>
            </span>
            <span>
              <b>{p.assigned_to_name || "Unassigned"}</b>
              <small>
                {title(p.priority)} ·{" "}
                {p.estimated_minutes
                  ? `${p.estimated_minutes} min`
                  : "No estimate"}
              </small>
            </span>
            <em className={p.active ? "active" : "paused"}>
              {p.active ? "Active" : "Paused"}
            </em>
            {data.canManage ? (
              <div>
                <button
                  className="secondary"
                  onClick={async () => {
                    const details = await call(
                      `/api/ppm/plans/${p.id}/requirements`,
                    );
                    setRequirements({ ...p, ...details });
                  }}
                >
                  Checklist &amp; parts
                </button>
                <button
                  className="secondary"
                  onClick={() => {
                    setEdit(p);
                    setCreate(false);
                  }}
                >
                  Edit
                </button>
                <button
                  className="secondary"
                  onClick={() =>
                    action(`/api/ppm/plans/${p.id}/toggle`, {
                      active: !p.active,
                    })
                  }
                >
                  {p.active ? (
                    <>
                      <Pause />
                      Pause
                    </>
                  ) : (
                    <>
                      <Play />
                      Activate
                    </>
                  )}
                </button>
              </div>
            ) : null}
          </article>
        ))}
      </section>
      {dismiss ? (
        <PpmDismissDialog
          occurrence={dismiss}
          close={() => setDismiss(null)}
          save={async (reason: string) => {
            await action(`/api/ppm/occurrences/${dismiss.id}/dismiss`, { reason });
            setDismiss(null);
          }}
        />
      ) : null}
    </>
  );
}

function PpmDismissDialog({ occurrence, close, save }: any) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return <div className="cmms-modal" role="dialog" aria-modal="true"><form className="cmms-temp-form" onSubmit={async(e)=>{e.preventDefault();setBusy(true);await save(reason.trim());setBusy(false)}}><div><h2>Dismiss PPM occurrence</h2><p>{occurrence.plan_name} · due {day(occurrence.due_at)}. The reason will be retained for audit.</p></div><label>Reason<textarea autoFocus required minLength={3} rows={5} value={reason} onChange={e=>setReason(e.target.value)} /></label><div className="cmms-modal-actions"><button type="button" className="secondary" onClick={close}>Cancel</button><button disabled={busy}>{busy ? "Saving…" : "Dismiss occurrence"}</button></div></form></div>;
}

function PpmForm({ value, assets, users, jobTypes, saved }: any) {
  const initial = {
    name: value?.name || "",
    assetId: value?.asset_id || "",
    instructions: value?.instructions || "",
    intervalValue: value?.interval_value || 1,
    intervalUnit: value?.interval_unit || "MONTH",
    priority: value?.priority || "MEDIUM",
    estimatedMinutes: value?.estimated_minutes || 60,
    assignedTo: value?.assigned_to || "",
    jobTypeId: value?.job_type_id || jobTypes[0]?.id || "",
    nextDueAt: value?.next_due_at
      ? new Date(value.next_due_at).toISOString().slice(0, 10)
      : new Date().toISOString().slice(0, 10),
    active: value?.active !== false,
  };
  const [f, setF] = useState(initial),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false),
    [checklistOpen, setChecklistOpen] = useState(false),
    [steps, setSteps] = useState<any[]>([]);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await call(value ? `/api/ppm/plans/${value.id}` : "/api/ppm/plans", {
        method: value ? "PUT" : "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({...f,steps: value ? undefined : steps.filter(x=>x.label.trim())}),
      });
      saved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="func-panel ppm-form">
      <div className="func-table-head">
        <h2>{value ? "Edit PPM schedule" : "Create PPM schedule"}</h2>
      </div>
      <form onSubmit={submit}>
        <label>
          Schedule name
          <input
            required
            value={f.name}
            onChange={(e) => setF({ ...f, name: e.target.value })}
          />
        </label>
        <label>
          Asset
          <select
            required
            value={f.assetId}
            onChange={(e) => setF({ ...f, assetId: e.target.value })}
          >
            <option value="">Choose asset…</option>
            {assets.map((a: any) => (
              <option key={a.id} value={a.id}>
                {a.path}
              </option>
            ))}
          </select>
        </label>
        <label>
          Job type
          <select
            required
            value={f.jobTypeId}
            onChange={(e) => setF({ ...f, jobTypeId: e.target.value })}
          >
            {jobTypes.map((type: any) => (
              <option key={type.id} value={type.id}>
                {type.name}
                {type.contractor ? " — contractor" : ""}
              </option>
            ))}
          </select>
        </label>
        <label>
          Assigned engineer
          <select
            value={f.assignedTo}
            onChange={(e) => setF({ ...f, assignedTo: e.target.value })}
          >
            <option value="">Unassigned</option>
            {users.map((u: any) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Repeat every
          <div>
            <input
              type="number"
              min="1"
              max="999"
              value={f.intervalValue}
              onChange={(e) =>
                setF({ ...f, intervalValue: Number(e.target.value) })
              }
            />
            <select
              value={f.intervalUnit}
              onChange={(e) => setF({ ...f, intervalUnit: e.target.value })}
            >
              <option value="DAY">Days</option>
              <option value="WEEK">Weeks</option>
              <option value="MONTH">Months</option>
              <option value="YEAR">Years</option>
            </select>
          </div>
        </label>
        <label>
          Next due date
          <input
            required
            type="date"
            value={f.nextDueAt}
            onChange={(e) => setF({ ...f, nextDueAt: e.target.value })}
          />
        </label>
        <label>
          Priority
          <select
            value={f.priority}
            onChange={(e) => setF({ ...f, priority: e.target.value })}
          >
            <option>LOW</option>
            <option>MEDIUM</option>
            <option>HIGH</option>
          </select>
        </label>
        <label>
          Estimated minutes
          <input
            type="number"
            min="1"
            value={f.estimatedMinutes}
            onChange={(e) =>
              setF({ ...f, estimatedMinutes: Number(e.target.value) })
            }
          />
        </label>
        <label className="wide">
          Maintenance instructions
          <textarea
            required
            value={f.instructions}
            onChange={(e) => setF({ ...f, instructions: e.target.value })}
            placeholder="Isolation, checks, lubrication, tolerances, replacement tasks…"
          />
        </label>
        {!value ? <div className="wide"><button type="button" className="secondary" onClick={()=>setChecklistOpen(true)}>Add checklist {steps.length ? `(${steps.length} fields)` : ''}</button><p>Optional: add ticks, measurements and written responses to every work order from this schedule.</p></div> : null}
        {error ? <p className="ppm-error">{error}</p> : null}
        <button disabled={busy}>
          {busy ? "Saving…" : "Save PPM schedule"}
        </button>
      </form>
      {checklistOpen ? <div className="cmms-modal" role="dialog" aria-modal="true" aria-label="PPM checklist editor"><div className="cmms-temp-form ppm-checklist-modal"><h2>PPM checklist</h2><p>Choose what engineers must record before completing the job.</p>{steps.map((step:any,index:number)=><div className="ppm-step-row" key={index}><input aria-label={`Step ${index+1}`} placeholder="Check or measurement" value={step.label} onChange={e=>setSteps(steps.map((x,i)=>i===index?{...x,label:e.target.value}:x))}/><select aria-label={`Response type ${index+1}`} value={step.responseType} onChange={e=>setSteps(steps.map((x,i)=>i===index?{...x,responseType:e.target.value}:x))}><option value="CHECK">Tick</option><option value="NUMBER">Value</option><option value="TEXT">Text</option></select>{step.responseType==='NUMBER'?<input aria-label={`Unit ${index+1}`} placeholder="Unit" value={step.unit} onChange={e=>setSteps(steps.map((x,i)=>i===index?{...x,unit:e.target.value}:x))}/>:null}<button type="button" className="secondary" onClick={()=>setSteps(steps.filter((_,i)=>i!==index))}>Remove</button></div>)}<button type="button" className="secondary" onClick={()=>setSteps([...steps,{label:'',responseType:'CHECK',unit:''}])}>Add field</button><div className="cmms-modal-actions"><button type="button" onClick={()=>{setSteps(steps.filter(x=>x.label.trim()));setChecklistOpen(false)}}>Done</button></div></div></div> : null}
    </section>
  );
}

function RequirementsEditor({ plan, close, saved }: any) {
  const [steps, setSteps] = useState<any[]>(plan.steps.map((x: any) => ({label:x.label,responseType:x.response_type || 'CHECK',unit:x.unit || ''}))),
    [toolsRequired, setTools] = useState(plan.tools_required || ""),
    [safetyInstructions, setSafety] = useState(plan.safety_instructions || ""),
    [parts, setParts] = useState<any[]>(plan.parts || []),
    [error, setError] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await call(`/api/ppm/plans/${plan.id}/requirements`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          steps: steps.filter((x: any) => x.label.trim()),
          toolsRequired,
          safetyInstructions,
          parts: parts.map((x) => ({
            partId: x.part_id,
            quantity: Number(x.quantity),
          })),
        }),
      });
      saved();
    } catch (e: any) {
      setError(e.message);
    }
  }
  return (
    <section className="func-panel ppm-form">
      <div className="func-table-head">
        <h2>{plan.name}: job requirements</h2>
        <button className="secondary" onClick={close}>
          Cancel
        </button>
      </div>
      <form onSubmit={submit}>
        <div className="wide ppm-step-editor">
          <strong>Mandatory checklist</strong>
          {steps.map((step:any,index:number) => <div className="ppm-step-row" key={index}>
            <input aria-label={`Step ${index+1}`} placeholder="Inspection or measurement" value={step.label} onChange={e=>setSteps(steps.map((x,i)=>i===index?{...x,label:e.target.value}:x))}/>
            <select aria-label={`Response type ${index+1}`} value={step.responseType} onChange={e=>setSteps(steps.map((x,i)=>i===index?{...x,responseType:e.target.value}:x))}><option value="CHECK">Tick</option><option value="NUMBER">Value</option><option value="TEXT">Text</option></select>
            {step.responseType==='NUMBER'?<input aria-label={`Unit ${index+1}`} placeholder="Unit, e.g. °C" value={step.unit} onChange={e=>setSteps(steps.map((x,i)=>i===index?{...x,unit:e.target.value}:x))}/>:null}
            <button type="button" className="secondary" onClick={()=>setSteps(steps.filter((_,i)=>i!==index))}>Remove</button>
          </div>)}
          <button type="button" className="secondary" onClick={()=>setSteps([...steps,{label:'',responseType:'CHECK',unit:''}])}>Add checklist field</button>
        </div>
        <label className="wide">
          Safety and isolation instructions
          <textarea
            value={safetyInstructions}
            onChange={(e) => setSafety(e.target.value)}
          />
        </label>
        <label className="wide">
          Tools and equipment required
          <textarea
            value={toolsRequired}
            onChange={(e) => setTools(e.target.value)}
          />
        </label>
        <label className="wide">
          Required spare parts
          <select
            defaultValue=""
            onChange={(e) => {
              const item = plan.catalog.find(
                (x: any) => x.id === e.target.value,
              );
              if (item && !parts.some((x: any) => x.part_id === item.id))
                setParts([
                  ...parts,
                  {
                    part_id: item.id,
                    canonical_name: item.canonical_name,
                    primary_code: item.primary_code,
                    quantity: 1,
                  },
                ]);
              e.target.value = "";
            }}
          >
            <option value="">Add a part…</option>
            {plan.catalog.map((x: any) => (
              <option key={x.id} value={x.id}>
                {x.canonical_name} {x.primary_code ? `(${x.primary_code})` : ""}
              </option>
            ))}
          </select>
        </label>
        <div className="wide">
          {parts.map((x: any) => (
            <div key={x.part_id}>
              <span>{x.canonical_name}</span>
              <input
                aria-label={`Quantity for ${x.canonical_name}`}
                type="number"
                min="0.001"
                step="0.001"
                value={x.quantity}
                onChange={(e) =>
                  setParts(
                    parts.map((p: any) =>
                      p.part_id === x.part_id
                        ? { ...p, quantity: e.target.value }
                        : p,
                    ),
                  )
                }
              />
              <button
                type="button"
                className="secondary"
                onClick={() =>
                  setParts(parts.filter((p: any) => p.part_id !== x.part_id))
                }
              >
                Remove
              </button>
            </div>
          ))}
        </div>
        {error ? <p className="ppm-error">{error}</p> : null}
        <button>Save requirements</button>
      </form>
    </section>
  );
}
